import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPostsWithSlugs, getPriceCascades, updatePriceCascade } from '@/services/api';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, ArrowDown, FileCode, Globe, Loader2, Pencil, X } from 'lucide-react';
import type { CascadeGranulation, CascadeStepsConfig, PriceCascade, UpdatePriceCascadeInput } from '@/types/price-rules';
import type { Domain } from '@/types/domain';
import { GranulationSelect, PostPicker, SectionLabel, cascadeKey, normalizeSlug } from './CascadeModalParts';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cascade: PriceCascade | null;
    domains: Domain[] | undefined;
    granulations: CascadeGranulation[] | undefined;
    cascadeStepsConfig: CascadeStepsConfig | undefined;
}

/** Destino do redirect: ID do post quando conhecido; `fallback` guarda a slug gravada ate achar o ID */
type DestState = { id: string; fallback: string | null };

/**
 * Edita UMA cascata no mesmo visual do modal de criacao. O ad unit e o dominio
 * ficam fixos; so o que mudou vai no PUT. Pais, modo de blocos, timeout e os
 * modos de redirect/trigger nao aparecem e continuam como estao gravados —
 * quando diferem do padrao, o modal avisa que foram mantidos.
 */
export default function EditCascadeModal({ open, onOpenChange, cascade, domains, granulations, cascadeStepsConfig }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [post, setPost] = useState('');
    const [granulationId, setGranulationId] = useState<number | null>(null);
    const [dest, setDest] = useState<DestState>({ id: '', fallback: null });
    const [granulationP2, setGranulationP2] = useState<number | null>(null);

    const saveMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePriceCascadeInput; adUnit: string }) => updatePriceCascade(id, data),
        onSuccess: (_res, vars) => {
            queryClient.invalidateQueries({ queryKey: ['price-cascades'] });
            toast({ title: `Cascata ${vars.adUnit} atualizada` });
            onOpenChange(false);
        },
        onError: (err: any) => {
            toast({ title: 'Erro ao atualizar cascata', description: err?.response?.data?.detail || 'Erro desconhecido', variant: 'destructive' });
        },
    });

    const originalSlug = normalizeSlug(cascade?.redirect_url) || null;

    useEffect(() => {
        if (!open || !cascade) return;
        setPost(cascade.id_post_wp || '');
        setGranulationId(cascade.granulation_id);
        setDest({ id: '', fallback: normalizeSlug(cascade.redirect_url) || null });
        setGranulationP2(cascade.granulation_id_p2);
    }, [open, cascade]);

    const domainId = cascade?.domain_id ?? null;
    const isReward = /^rewarded$/i.test(cascade?.ad_unit || '');

    const { data: postsData, isLoading: postsLoading } = useQuery({
        queryKey: ['posts-with-slugs', domainId],
        queryFn: () => getPostsWithSlugs(domainId!),
        enabled: open && !!domainId,
    });
    const posts = postsData?.posts || [];

    const { data: domainCascades } = useQuery({
        queryKey: ['price-cascades', domainId],
        queryFn: () => getPriceCascades(domainId!),
        enabled: open && !!domainId,
    });

    const slugOf = useMemo(() => {
        const map = new Map<string, string>();
        for (const p of posts) if (p.slug) map.set(p.id_post_wp, p.slug);
        for (const c of domainCascades || []) if (c.id_post_wp && c.slug) map.set(c.id_post_wp, c.slug);
        return (id: string) => map.get(id) || null;
    }, [posts, domainCascades]);

    // O redirect so guarda a slug: acha o ID do destino quando posts/cascatas carregarem
    useEffect(() => {
        if (!dest.fallback || dest.id) return;
        const fromCascades = (domainCascades || []).find(c => c.id_post_wp && normalizeSlug(c.slug) === dest.fallback);
        const fromPosts = posts.find(p => normalizeSlug(p.slug) === dest.fallback);
        const id = fromCascades?.id_post_wp || fromPosts?.id_post_wp;
        if (id) setDest(d => ({ ...d, id }));
    }, [dest, posts, domainCascades]);

    if (!cascade) return null;

    const p = post.trim();
    const destId = dest.id.trim();
    const hasDest = !!destId || !!dest.fallback;
    // `fallback` preenchido = continua o destino que está gravado; digitar/limpar o zera
    const destChanged = isReward && dest.fallback === null && (!!destId || !!originalSlug);

    const conflict = (domainCascades || []).find(c => c.id !== cascade.id && cascadeKey(c.id_post_wp, c.ad_unit) === cascadeKey(p || null, cascade.ad_unit));

    const postChanged = (cascade.id_post_wp || '') !== p;
    const granulationChanged = cascade.granulation_id !== granulationId;
    const p2Changed = isReward && cascade.granulation_id_p2 !== granulationP2;
    const changed = postChanged || granulationChanged || destChanged || p2Changed;

    const errors: string[] = [];
    const warnings: string[] = [];
    if (conflict) {
        errors.push(`Já existe ${cascade.ad_unit} em ${conflict.slug ? `/${conflict.slug}` : p ? `post ${p}` : 'global'}`);
    }
    if (isReward && destId && destId === p) errors.push('O post de destino não pode ser o próprio post de origem');
    if (!p && postChanged) warnings.push('Sem post, a regra passa a valer para todos os posts do site');
    if (isReward && originalSlug && !hasDest) warnings.push('Sem destino, o Rewarded deixa de redirecionar');

    // Campos que o modal nao mostra: avisa quando fogem do padrao, para ninguem achar que sumiram
    const kept: string[] = [];
    if (cascade.country && cascade.country !== '*') kept.push(`país ${cascade.country}`);
    if (cascade.block_mode && cascade.block_mode !== 'standard') kept.push('Cascade Blocks');
    if (cascade.timeout && cascade.timeout !== 30000) kept.push(`timeout ${(cascade.timeout / 1000).toFixed(0)}s`);
    if (isReward && cascade.trigger_mode === 'direct') kept.push('trigger Direct');
    if (isReward && cascade.redirect_mode === 'normal') kept.push('redirect Normal');

    const handleSave = () => {
        if (!changed || errors.length) return;
        const data: UpdatePriceCascadeInput = {};
        if (postChanged) data.id_post_wp = p || null;
        if (granulationChanged) data.granulation_id = granulationId;
        // Só o ID vai: a slug (e os modos, se faltarem) a API resolve no WordPress
        if (destChanged) data.destination_post = destId || null;
        if (p2Changed) data.granulation_id_p2 = granulationP2;
        saveMutation.mutate({ id: cascade.id, data, adUnit: cascade.ad_unit });
    };

    const domain = domains?.find(d => d.id === cascade.domain_id);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-blue-100">
                            <Pencil className="h-4 w-4 text-blue-600" />
                        </div>
                        Editar Cascata
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{cascade.ad_unit}</span>
                    </DialogTitle>
                    <DialogDescription>Altere a configuração desta cascata</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="space-y-2">
                        <SectionLabel icon={<Globe className="h-3.5 w-3.5 text-blue-600" />} tone="bg-blue-100">Domínio</SectionLabel>
                        <div className="flex items-center gap-2 px-3 h-10 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700">
                            {domain && <img src={`https://www.google.com/s2/favicons?domain=${domain.domain}&sz=32`} className="h-4 w-4 rounded" alt="" />}
                            {domain?.domain ?? `ID: ${cascade.domain_id}`}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <SectionLabel icon={<FileCode className="h-3.5 w-3.5 text-indigo-600" />} tone="bg-indigo-100">
                            Post #1 <span className="font-normal text-gray-400">· origem</span>
                        </SectionLabel>
                        <PostPicker
                            value={post}
                            onChange={setPost}
                            posts={posts}
                            loading={postsLoading}
                            hasDomain
                            slugOf={slugOf}
                            takenIds={destId ? [destId] : []}
                        />
                        <UnitRow label={cascade.ad_unit}>
                            <GranulationSelect
                                value={granulationId}
                                onChange={setGranulationId}
                                adUnit={cascade.ad_unit}
                                granulations={granulations}
                                cascadeStepsConfig={cascadeStepsConfig}
                                className="ml-auto h-8 w-[210px] text-xs"
                            />
                        </UnitRow>
                    </div>

                    {isReward && (
                        <>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                <div className="h-px flex-1 bg-gray-200" />
                                <ArrowDown className="h-3.5 w-3.5" />
                                redireciona para
                                <div className="h-px flex-1 bg-gray-200" />
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <SectionLabel icon={<FileCode className="h-3.5 w-3.5 text-amber-600" />} tone="bg-amber-100">
                                        Post #2 <span className="font-normal text-gray-400">· destino</span>
                                    </SectionLabel>
                                    <span className="text-xs text-gray-400 ml-auto">opcional</span>
                                </div>

                                {dest.fallback && !destId && (
                                    <div className="flex items-center gap-2 text-xs text-amber-700">
                                        <span className="truncate">Destino atual: /{dest.fallback}/{postsLoading ? ' — procurando o post…' : ' — ID não encontrado na lista'}</span>
                                        <button type="button" className="ml-auto shrink-0 font-medium text-gray-500 hover:text-gray-700" onClick={() => setDest({ id: '', fallback: null })}>
                                            Remover
                                        </button>
                                    </div>
                                )}

                                <PostPicker
                                    value={dest.id}
                                    onChange={(v) => setDest({ id: v, fallback: null })}
                                    posts={posts}
                                    loading={postsLoading}
                                    hasDomain
                                    slugOf={slugOf}
                                    takenIds={p ? [p] : []}
                                />

                                <div className={hasDest ? '' : 'opacity-50 pointer-events-none'}>
                                    <UnitRow label="Rewarded" note="pós-redirect">
                                        <GranulationSelect
                                            value={granulationP2}
                                            onChange={setGranulationP2}
                                            adUnit="Rewarded"
                                            granulations={granulations}
                                            cascadeStepsConfig={cascadeStepsConfig}
                                            inheritLabel="Usar a da origem"
                                            disabled={!hasDest}
                                            className="ml-auto h-8 w-[210px] text-xs"
                                        />
                                    </UnitRow>
                                </div>
                            </div>
                        </>
                    )}

                    {kept.length > 0 && (
                        <p className="text-xs text-gray-500">Mantidos como estão: {kept.join(' · ')}</p>
                    )}

                    {(errors.length > 0 || warnings.length > 0) && (
                        <div className="space-y-1">
                            {errors.map(e => (
                                <p key={e} className="flex items-start gap-1.5 text-xs text-red-600"><X className="h-3.5 w-3.5 shrink-0 mt-px" />{e}</p>
                            ))}
                            {warnings.map(w => (
                                <p key={w} className="flex items-start gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}</p>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                        {!changed && <span className="text-xs text-gray-400 mr-auto">Nada alterado</span>}
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={!changed || errors.length > 0 || saveMutation.isPending}>
                            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            Salvar
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function UnitRow({ label, note, children }: { label: string; note?: string; children: ReactNode }) {
    return (
        <div className="border border-gray-200 rounded-lg flex items-center gap-3 px-3 py-2">
            <div className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{label}</span>
                {note && <span className="block text-[11px] leading-tight text-gray-400">{note}</span>}
            </div>
            {children}
        </div>
    );
}
